// Template compiler: turns a parsed PSD (ag-psd, read with `useImageData`) into
// the baked assets the renderers need. Pure JavaScript, no canvas or DOM, so it
// runs in a Web Worker, on the main thread, or in Node tests.
//
// Final image model (per output pixel, per channel):
//   d       = design colour (user artwork after adjustments)
//   shaded  = d * lightGain + lightBias          (clipped lighting layers)
//   mixed   = mix(below, B(below, shaded), a)    (design blend mode B, alpha a)
//   final   = mixed * postGain + postBias        (non-clipped layers above)
// where a = artwork alpha * coverage * transparency setting.
//
// Outside the design surface (coverage 0) `below` holds the PSD's own composite
// and post is the identity, so those pixels match Photoshop exactly.

import { bakeMap, forwardGrid, packUV, supported } from "./geometry.js";
import { AFFINE_EXACT, BLEND_CODES, affineForChannel, blendName, blendPixel, isApproximatedAlias } from "./blendModes.js";

export const TEMPLATE_VERSION = 1;
const STRIP = 64;

// ---------------------------------------------------------------------------
// Layer tree normalization

function toBytes(data) {
  if (!data) return null;
  if (data instanceof Uint8ClampedArray || data instanceof Uint8Array) return data;
  const out = new Uint8ClampedArray(data.length);
  if (data instanceof Uint16Array) for (let i = 0; i < data.length; i++) out[i] = data[i] >> 8;
  else for (let i = 0; i < data.length; i++) out[i] = Math.round(Math.max(0, Math.min(1, data[i])) * 255);
  return out;
}

function rasterOf(layer) {
  const img = layer.imageData;
  if (!img || !img.width || !img.height) return null;
  return { left: layer.left || 0, top: layer.top || 0, width: img.width, height: img.height, data: toBytes(img.data) };
}

function maskOf(layer) {
  const m = layer.mask;
  if (!m || m.disabled) return null;
  const img = m.imageData;
  const base = { defaultValue: (m.defaultColor ?? 0) / 255 };
  if (!img || !img.width || !img.height) return { ...base, left: 0, top: 0, width: 0, height: 0, data: null };
  return { ...base, left: m.left || 0, top: m.top || 0, width: img.width, height: img.height, data: toBytes(img.data) };
}

const layerOpacity = (layer) => (layer.opacity ?? 1) * (layer.fillOpacity ?? 1);

/**
 * Finds the design Smart Object: a placed layer named "Design" (any depth,
 * case-insensitive), otherwise the first placed layer.
 */
export function findDesignLayer(psd) {
  let named = null, first = null;
  (function walk(layers, parents) {
    for (const layer of layers || []) {
      if (layer.placedLayer) {
        if (!first) first = { layer, parents };
        if (!named && /^\s*design\s*$/i.test(layer.name || "")) named = { layer, parents };
      }
      if (layer.children) walk(layer.children, [...parents, layer]);
    }
  })(psd.children, []);
  return named || first;
}

/**
 * Flattens pass-through groups on the path to the design so that the design,
 * its clipped layers and everything above/below sit in one list. Other groups
 * stay as nodes and are composited in isolation.
 */
function buildNodes(layers, design, warnings, inherited = 1) {
  const nodes = [];
  for (const layer of layers || []) {
    if (layer.hidden) continue;
    const opacity = layerOpacity(layer) * inherited;
    if (layer.children) {
      const containsDesign = design && containsLayer(layer, design);
      if (containsDesign) {
        if (layer.mask && !layer.mask.disabled) warnings.add("The group containing the design has a mask; it is ignored.");
        if ((layer.blendMode || "pass through") !== "pass through" && blendName(layer.blendMode) !== "normal") {
          warnings.add("The group containing the design uses a blend mode; it is approximated.");
        }
        nodes.push(...buildNodes(layer.children, design, warnings, opacity));
      } else {
        const passThrough = (layer.blendMode || "pass through") === "pass through";
        nodes.push({
          kind: "group",
          name: layer.name,
          clipped: !!layer.clipping,
          blend: passThrough ? "normal" : blendName(layer.blendMode),
          passThrough,
          opacity,
          mask: maskOf(layer),
          children: buildNodes(layer.children, null, warnings, 1),
        });
      }
      continue;
    }
    if (layer === design) {
      nodes.push({ kind: "design", layer, opacity, clipped: false });
      continue;
    }
    if (layer.adjustment) {
      warnings.add(`Adjustment layer “${layer.name}” is not applied inside the design area.`);
      continue;
    }
    if (isApproximatedAlias(layer.blendMode)) warnings.add(`Blend mode “${layer.blendMode}” on “${layer.name}” is approximated.`);
    const raster = rasterOf(layer);
    if (!raster) continue;
    nodes.push({ kind: "raster", name: layer.name, clipped: !!layer.clipping, blend: blendName(layer.blendMode), opacity, raster, mask: maskOf(layer) });
  }
  return nodes;
}

function containsLayer(group, target) {
  for (const child of group.children || []) {
    if (child === target || (child.children && containsLayer(child, target))) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Resampling grid: output pixel o covers source pixels [start[o], end[o]).

function spans(src, out) {
  const start = new Int32Array(out), end = new Int32Array(out);
  for (let o = 0; o < out; o++) {
    start[o] = Math.floor((o * src) / out);
    end[o] = Math.max(start[o] + 1, Math.floor(((o + 1) * src) / out));
  }
  return { start, end };
}

/**
 * Rasterizes rows [y0, y1) of a layer onto the output grid as premultiplied
 * floats (box-filtered when scaling down). `out` holds (y1 - y0) * outW * 4.
 */
function rasterRows(raster, mask, grid, y0, y1, out) {
  out.fill(0);
  if (!raster) return out;
  const { cols, rows, outW } = grid;
  const { left, top, width, height, data } = raster;
  // Output columns that can touch the layer.
  let oxStart = 0, oxEnd = outW;
  while (oxStart < outW && cols.end[oxStart] <= left) oxStart++;
  while (oxEnd > oxStart && cols.start[oxEnd - 1] >= left + width) oxEnd--;
  for (let oy = y0; oy < y1; oy++) {
    const sy0 = Math.max(rows.start[oy], top), sy1 = Math.min(rows.end[oy], top + height);
    if (sy1 <= sy0) continue;
    const rowArea = rows.end[oy] - rows.start[oy];
    const base = (oy - y0) * outW * 4;
    for (let ox = oxStart; ox < oxEnd; ox++) {
      const sx0 = Math.max(cols.start[ox], left), sx1 = Math.min(cols.end[ox], left + width);
      if (sx1 <= sx0) continue;
      let r = 0, g = 0, b = 0, a = 0;
      for (let sy = sy0; sy < sy1; sy++) {
        let p = ((sy - top) * width + (sx0 - left)) * 4;
        for (let sx = sx0; sx < sx1; sx++, p += 4) {
          let alpha = data[p + 3];
          if (!alpha) continue;
          alpha /= 255;
          if (mask) alpha *= maskValue(mask, sx, sy);
          r += data[p] * alpha;
          g += data[p + 1] * alpha;
          b += data[p + 2] * alpha;
          a += alpha;
        }
      }
      const area = rowArea * (cols.end[ox] - cols.start[ox]), k = base + ox * 4;
      out[k] = r / 255 / area;
      out[k + 1] = g / 255 / area;
      out[k + 2] = b / 255 / area;
      out[k + 3] = a / area;
    }
  }
  return out;
}

function maskValue(mask, x, y) {
  const mx = x - mask.left, my = y - mask.top;
  if (!mask.data || mx < 0 || my < 0 || mx >= mask.width || my >= mask.height) return mask.defaultValue;
  return mask.data[(my * mask.width + mx) * 4] / 255;
}

/** Averages a mask (no layer pixels) onto rows of the output grid. */
function maskRows(mask, grid, y0, y1, out) {
  const { cols, rows, outW } = grid;
  for (let oy = y0; oy < y1; oy++) {
    for (let ox = 0; ox < outW; ox++) {
      let sum = 0, n = 0;
      for (let sy = rows.start[oy]; sy < rows.end[oy]; sy++) {
        for (let sx = cols.start[ox]; sx < cols.end[ox]; sx++, n++) sum += maskValue(mask, sx, sy);
      }
      out[(oy - y0) * outW + ox] = sum / n;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Strip compositing (straight RGBA floats in [0, 1]).

const tmpB = [0, 0, 0], tmpS = [0, 0, 0], tmpO = [0, 0, 0], affine = [0, 0];

function unpremultiply(buf) {
  for (let i = 0; i < buf.length; i += 4) {
    const a = buf[i + 3];
    if (a > 1e-6) {
      buf[i] /= a;
      buf[i + 1] /= a;
      buf[i + 2] /= a;
    } else buf[i] = buf[i + 1] = buf[i + 2] = buf[i + 3] = 0;
  }
  return buf;
}

/** Composites `src` over `dst` with a blend mode (W3C compositing model). */
function composite(dst, src, blend, opacity, maskStrip) {
  const code = BLEND_CODES[blend] ?? 0;
  for (let i = 0, p = 0; i < dst.length; i += 4, p++) {
    let as = src[i + 3] * opacity;
    if (maskStrip) as *= maskStrip[p];
    if (as <= 0) continue;
    const ab = dst[i + 3];
    const ao = as + ab * (1 - as);
    tmpB[0] = dst[i]; tmpB[1] = dst[i + 1]; tmpB[2] = dst[i + 2];
    tmpS[0] = src[i]; tmpS[1] = src[i + 1]; tmpS[2] = src[i + 2];
    const B = code === 0 ? tmpS : blendPixel(code, tmpB, tmpS, tmpO);
    for (let c = 0; c < 3; c++) {
      dst[i + c] = (as * (1 - ab) * tmpS[c] + as * ab * B[c] + (1 - as) * ab * tmpB[c]) / ao;
    }
    dst[i + 3] = ao;
  }
}

/** Clipped composite: blends `src` into `dst` keeping `dst` alpha. */
function compositeClipped(dst, src, blend, opacity) {
  const code = BLEND_CODES[blend] ?? 0;
  for (let i = 0; i < dst.length; i += 4) {
    const as = src[i + 3] * opacity;
    if (as <= 0 || dst[i + 3] <= 0) continue;
    tmpB[0] = dst[i]; tmpB[1] = dst[i + 1]; tmpB[2] = dst[i + 2];
    tmpS[0] = src[i]; tmpS[1] = src[i + 1]; tmpS[2] = src[i + 2];
    const B = code === 0 ? tmpS : blendPixel(code, tmpB, tmpS, tmpO);
    for (let c = 0; c < 3; c++) dst[i + c] = (1 - as) * tmpB[c] + as * B[c];
  }
}

/** Groups each non-clipped node with the clipped nodes that follow it. */
function clipGroups(nodes) {
  const groups = [];
  for (const node of nodes) {
    if (node.clipped && groups.length) groups[groups.length - 1].clipped.push(node);
    else groups.push({ base: node, clipped: [] });
  }
  return groups;
}

class StripRenderer {
  constructor(grid) {
    this.grid = grid;
    this.pool = [];
  }
  buffer() {
    return this.pool.pop() || new Float32Array(this.grid.outW * STRIP * 4);
  }
  release(buf) {
    this.pool.push(buf);
  }
  maskStrip(mask, y0, y1) {
    if (!mask) return null;
    const out = new Float32Array(this.grid.outW * (y1 - y0));
    return maskRows(mask, this.grid, y0, y1, out);
  }
  /** Renders a single node (with its clipped layers) into an isolated buffer. */
  isolated(group, y0, y1) {
    const { base, clipped } = group;
    const buf = this.buffer();
    if (base.kind === "group") {
      buf.fill(0);
      this.stack(base.children, buf, y0, y1);
      const m = this.maskStrip(base.mask, y0, y1);
      if (m) for (let i = 0, p = 0; i < buf.length; i += 4, p++) buf[i + 3] *= m[p] ?? 0;
    } else {
      unpremultiply(rasterRows(base.raster, base.mask, this.grid, y0, y1, buf));
    }
    for (const c of clipped) {
      const src = this.isolated({ base: c, clipped: [] }, y0, y1);
      compositeClipped(buf, src, c.blend, c.opacity);
      this.release(src);
    }
    return buf;
  }
  /** Composites a node list onto `dst`. */
  stack(nodes, dst, y0, y1) {
    for (const group of clipGroups(nodes)) {
      const { base } = group;
      if (base.kind === "design") continue;
      if (base.kind === "group" && base.passThrough && base.opacity >= 1 && !base.mask && !group.clipped.length) {
        this.stack(base.children, dst, y0, y1);
        continue;
      }
      const src = this.isolated(group, y0, y1);
      composite(dst, src, base.blend, base.opacity);
      this.release(src);
    }
  }
}

// ---------------------------------------------------------------------------
// Compilation

function compositeSource(psd) {
  const img = psd.imageData;
  if (!img || img.width !== psd.width || img.height !== psd.height) return null;
  return { left: 0, top: 0, width: img.width, height: img.height, data: toBytes(img.data) };
}

/**
 * Inspects a parsed PSD and returns the design target, or throws a
 * user-readable error when the template cannot be converted.
 */
export function inspectTemplate(psd) {
  if (!psd?.width || !psd?.height) throw new Error("This file is not a readable Photoshop document.");
  if (psd.colorMode !== undefined && psd.colorMode !== 3 && psd.colorMode !== 1) {
    throw new Error("Only RGB or grayscale Photoshop documents are supported.");
  }
  const target = findDesignLayer(psd);
  if (!target) throw new Error("No design Smart Object was found. Name your Smart Object layer “Design”.");
  const { layer } = target;
  const reason = supported(layer.placedLayer);
  if (reason) throw new Error(reason);
  if (!(layer.placedLayer.width > 0 && layer.placedLayer.height > 0)) throw new Error("The design Smart Object has no size.");
  const warnings = [];
  if (layer.vectorMask) warnings.push("The design layer's vector mask is not applied.");
  if (layer.effects && Object.keys(layer.effects).some((k) => !["scale", "disabled"].includes(k) && !layer.effects.disabled)) {
    warnings.push("Layer effects on the design layer are not applied.");
  }
  if (layer.placedLayer.filter || layer.filterEffectsMasks) warnings.push("Smart filters on the design layer are not applied.");
  if (!/^\s*design\s*$/i.test(layer.name || "")) warnings.push(`Using Smart Object “${layer.name}” as the design layer.`);
  return { layer, placed: layer.placedLayer, warnings };
}

/**
 * Compiles a parsed PSD into a render template.
 * @param psd       ag-psd document read with { useImageData: true }
 * @param options.maxSize   longest output edge (defaults to the PSD size)
 * @param options.progress  callback(fraction, label)
 */
export async function compileTemplate(psd, { maxSize = Infinity, progress = () => {} } = {}) {
  const started = Date.now();
  const { layer: design, placed, warnings: inspectWarnings } = inspectTemplate(psd);
  const warnings = new Set(inspectWarnings);
  const docW = psd.width, docH = psd.height;
  const scale = Math.min(1, maxSize / Math.max(docW, docH));
  const outW = Math.max(1, Math.round(docW * scale)), outH = Math.max(1, Math.round(docH * scale));
  const grid = { outW, outH, cols: spans(docW, outW), rows: spans(docH, outH) };

  // 1. Surface mapping.
  progress(0.02, "Baking the surface mapping…");
  const map = await bakeMap(placed, outW, outH, (f) => progress(0.02 + f * 0.38, "Baking the surface mapping…"), 96, outW / docW);
  const coverage = map.coverage;

  // Design mask and opacity are baked into coverage.
  const designMask = maskOf(design);
  const designOpacity = layerOpacity(design);
  const nodes = buildNodes(psd.children, design, warnings);
  const designIndex = nodes.findIndex((n) => n.kind === "design");
  if (designIndex < 0) throw new Error("The design layer is hidden.");
  let groupOpacity = nodes[designIndex].opacity / designOpacity;
  if (!Number.isFinite(groupOpacity)) groupOpacity = 1;
  const below = nodes.slice(0, designIndex);
  let end = designIndex + 1;
  while (end < nodes.length && nodes[end].clipped) end++;
  const lighting = nodes.slice(designIndex + 1, end);
  const above = nodes.slice(end);

  // 2. Lighting, background and overlays, strip by strip.
  const pixels = outW * outH;
  const belowOut = new Uint8ClampedArray(pixels * 4);
  const lightGain = new Float32Array(pixels * 3).fill(1);
  const lightBias = new Float32Array(pixels * 3);
  const hasPost = above.length > 0;
  const postGain = hasPost ? new Float32Array(pixels * 3).fill(1) : null;
  const postBias = hasPost ? new Float32Array(pixels * 3) : null;
  const composite = compositeSource(psd);
  if (!composite) warnings.add("The PSD has no saved preview; the background is rebuilt from layers.");

  const renderer = new StripRenderer(grid);
  const strip = renderer.buffer(), compStrip = new Float32Array(outW * STRIP * 4);
  const maskStrip = new Float32Array(outW * STRIP);
  const designCode = BLEND_CODES[blendName(design.blendMode)] ?? 0;
  let approximated = false;

  for (let y0 = 0; y0 < outH; y0 += STRIP) {
    const y1 = Math.min(outH, y0 + STRIP), n = (y1 - y0) * outW;
    const o = y0 * outW;

    // Background below the design, flattened onto white.
    strip.fill(0);
    for (let i = 0; i < n * 4; i += 4) { strip[i] = strip[i + 1] = strip[i + 2] = 1; strip[i + 3] = 1; }
    renderer.stack(below, strip, y0, y1);

    // Lighting: affine per channel in the design colour. Every lighting layer
    // clips to the design itself.
    for (const node of lighting.map((base) => ({ base, clipped: [] }))) {
      const src = renderer.isolated(node, y0, y1);
      const code = BLEND_CODES[node.base.blend] ?? 0;
      if (!AFFINE_EXACT.has(code)) approximated = true;
      for (let p = 0; p < n; p++) {
        const a = src[p * 4 + 3] * node.base.opacity;
        if (a <= 0) continue;
        for (let c = 0; c < 3; c++) {
          affineForChannel(code, src[p * 4 + c], a, affine);
          const k = (o + p) * 3 + c;
          lightGain[k] *= affine[0];
          lightBias[k] = lightBias[k] * affine[0] + affine[1];
        }
      }
      renderer.release(src);
    }

    // Layers above the design: affine in the composited colour.
    if (hasPost) {
      for (const node of clipGroups(above)) {
        if (node.base.kind === "group" && node.base.passThrough && node.base.opacity >= 1 && !node.base.mask && !node.clipped.length) {
          // Pass-through groups above: flatten their children in order.
          for (const child of clipGroups(node.base.children)) applyPost(child);
        } else applyPost(node);
      }
    }

    // Design mask into coverage.
    if (designMask) maskRows(designMask, grid, y0, y1, maskStrip);
    // Exact Photoshop pixels outside the design surface.
    if (composite) unpremultiply(rasterRows(composite, null, grid, y0, y1, compStrip));

    for (let p = 0; p < n; p++) {
      const k = o + p;
      let cov = coverage[k] / 255;
      if (cov > 0) {
        cov *= designOpacity * groupOpacity;
        if (designMask) cov *= maskStrip[p];
        coverage[k] = Math.round(cov * 255);
      }
      const useComposite = composite && coverage[k] === 0;
      if (useComposite) {
        // Flatten the saved composite onto white, like the layer background.
        const a = compStrip[p * 4 + 3];
        for (let c = 0; c < 3; c++) belowOut[k * 4 + c] = Math.round((compStrip[p * 4 + c] * a + 1 - a) * 255);
      } else {
        for (let c = 0; c < 3; c++) belowOut[k * 4 + c] = Math.round(strip[p * 4 + c] * 255);
      }
      belowOut[k * 4 + 3] = 255;
      if (useComposite && hasPost) {
        for (let c = 0; c < 3; c++) { postGain[k * 3 + c] = 1; postBias[k * 3 + c] = 0; }
      }
    }
    progress(0.4 + (y1 / outH) * 0.55, "Preparing shadows, highlights, and texture…");
    if (((y0 / STRIP) & 3) === 3) await new Promise((r) => setTimeout(r, 0));

    function applyPost(node) {
      const src = renderer.isolated(node, y0, y1);
      const code = BLEND_CODES[node.base.blend] ?? 0;
      if (!AFFINE_EXACT.has(code)) approximated = true;
      for (let p = 0; p < n; p++) {
        const a = src[p * 4 + 3] * node.base.opacity;
        if (a <= 0) continue;
        for (let c = 0; c < 3; c++) {
          affineForChannel(code, src[p * 4 + c], a, affine);
          const k = (o + p) * 3 + c;
          postGain[k] *= affine[0];
          postBias[k] = postBias[k] * affine[0] + affine[1];
        }
      }
      renderer.release(src);
    }
  }
  if (approximated) warnings.add("Some lighting layers use blend modes that are approximated.");

  // 3. Encode for upload.
  const light = encodeAffine(lightGain, lightBias, pixels);
  const post = hasPost ? encodeAffine(postGain, postBias, pixels) : null;
  progress(1, "Ready");

  return {
    version: TEMPLATE_VERSION,
    docWidth: docW,
    docHeight: docH,
    width: outW,
    height: outH,
    scale: outW / docW,
    designWidth: placed.width,
    designHeight: placed.height,
    designBlend: designCode,
    uv: packUV(map.uv),
    coverage,
    below: belowOut,
    lightGain: light.gain,
    lightBias: light.bias,
    lightBiasSigned: light.signed,
    postGain: post?.gain || null,
    postBias: post?.bias || null,
    postBiasSigned: post?.signed || false,
    grid: forwardGrid(placed, 32, outW / docW),
    gridSize: 32,
    meshSteps: map.steps,
    warnings: [...warnings],
    compileMs: Date.now() - started,
  };
}

/**
 * Flattens all visible layers of a document (transparent background) with the
 * same compositor. Used for embedded Smart Object documents, whose saved
 * composite can be stale.
 */
export function renderDocument(psd, { maxSize = Infinity } = {}) {
  const scale = Math.min(1, maxSize / Math.max(psd.width, psd.height));
  const outW = Math.max(1, Math.round(psd.width * scale)), outH = Math.max(1, Math.round(psd.height * scale));
  const grid = { outW, outH, cols: spans(psd.width, outW), rows: spans(psd.height, outH) };
  const warnings = new Set();
  const nodes = buildNodes(psd.children, null, warnings);
  const renderer = new StripRenderer(grid);
  const strip = renderer.buffer(), out = new Uint8ClampedArray(outW * outH * 4);
  for (let y0 = 0; y0 < outH; y0 += STRIP) {
    const y1 = Math.min(outH, y0 + STRIP), n = (y1 - y0) * outW;
    strip.fill(0);
    renderer.stack(nodes, strip, y0, y1);
    for (let i = 0; i < n * 4; i++) out[y0 * outW * 4 + i] = Math.round(strip[i] * 255);
  }
  return { width: outW, height: outH, data: out, warnings: [...warnings] };
}

/** Packs float gain/bias into RGBA8. Bias is signed (0.5 offset) when needed. */
function encodeAffine(gain, bias, pixels) {
  let signed = false;
  for (let i = 0; i < bias.length; i++) if (bias[i] < -1 / 512) { signed = true; break; }
  const g = new Uint8ClampedArray(pixels * 4), b = new Uint8ClampedArray(pixels * 4);
  for (let p = 0; p < pixels; p++) {
    for (let c = 0; c < 3; c++) {
      g[p * 4 + c] = Math.round(gain[p * 3 + c] * 255);
      const v = bias[p * 3 + c];
      b[p * 4 + c] = Math.round((signed ? v * 0.5 + 0.5 : v) * 255);
    }
    g[p * 4 + 3] = 255;
    b[p * 4 + 3] = 255;
  }
  return { gain: g, bias: b, signed };
}

/** Typed arrays of a template, for zero-copy transfer between threads. */
export function templateTransferables(t) {
  return [t.uv, t.coverage, t.below, t.lightGain, t.lightBias, t.postGain, t.postBias, t.grid]
    .filter(Boolean)
    .map((a) => a.buffer);
}
